import type { FC } from "react";
import type { WidgetProps } from "@rjsf/utils";
import KioTextWidget from "@/framework/KioForm/widgets/KioTextWidget";
import type { KioFormContext } from "@/framework/KioForm/KioForm";

const getFormContextIdSchemaKey = (idSchemaId: string, omitLastN: number = 2): string => {
  return idSchemaId.split("_").slice(0, omitLastN).join("_");
};

/**
 * Generates a random value and checks if it is unique. Uses formContext.sharedState to store the unique values for a given idSchemaId.
 * If the value is not unique, it will generate a new value until it finds a unique one.
 * The unique value is then set as the value of the widget and passed to the onChange handler.
 *
 * The schemaId is default 2 levels lower than the current one.
 *
 * I.e:
 * kio-form-root_answers_0_answerId --> kio-form-root_answers (last 2 levels are omitted)
 *
 * Can be overriden with adding overrideLastN *
 *
 */
const UniqueValueWidget: FC<WidgetProps> = ({ ...props }) => {
  const formContext: KioFormContext = props.formContext ?? {};
  const uiOptions = props.uiSchema?.["ui:options"] ?? {};
  const overrideLastN: number | undefined = uiOptions?.overrideLastN; // How many levels down should be used as a common object
  const formContextIdSchemaKey = getFormContextIdSchemaKey(props.id, overrideLastN || 2);

  if (!formContext.sharedState[formContextIdSchemaKey]) {
    formContext.sharedState[formContextIdSchemaKey] = { randomValues: new Set() };
  }
  const getRandomValue = () => {
    return Math.floor(Math.random() * 100000)
      .toString(36)
      .padStart(3, "0");
  };
  const existingValues = formContext.sharedState[formContextIdSchemaKey].randomValues;
  if (!props.value) {
    let newValue: string;
    while (true) {
      newValue = getRandomValue();
      if (!existingValues.has(newValue)) {
        break;
      }
    }
    props.onChange(newValue);
    existingValues.add(newValue);
  }
  existingValues.add(String(props.value));

  return <KioTextWidget {...props} disabled />;
};

export default UniqueValueWidget;
